import {
  groupBy as _groupBy,
  memoize as _memoize,
  sortBy as _sortBy,
  uniq as _uniq,
} from 'lodash/fp'

import { DeviceType } from '@flowx/designer/modules/ui-designer/models/device-type.enum'
import { UiDesignerContext } from '@flowx/designer/modules/ui-designer/models/ui-designer-context.enum'
import { SUPPORTED_PLATFORMS2 } from '@flowx/designer/modules/ui-designer/node-ui.store'
import {
  allowedChildComponents,
  allowedUiFlowRootComponents,
  webOnlyComponents,
} from '@flowx/designer/modules/ui-designer/services/templateConfig.entity'
import { ProcessPlatform } from '@flowx/designer/shared/entities/misconfig.entity'
import {
  EventHandler,
  EventHandlerActionType,
  EventHandlerTriggerEvent,
} from '@flowx/designer/shared/types/event-handler.types'
import {
  findTemplateConfigDescendantWith,
  templateConfigAiIdentity,
} from '@flowx/designer/shared/utils/template-config.utils'
import {
  ComponentIdentifierEnum,
  FormFieldsEnum,
  TemplateConfigInterface,
} from '@flowx/shared/interfaces'
import { DimensionTypes, LayoutType } from '@flowx/ui-theme'

import {
  AI_PLATFORM_CODE,
  AiPlatformCode,
  AiTemplateConfig,
  AiUiInterface,
  AiUiTemplateElement,
  AiValidator,
  DesignerService,
  UiFlowLinkButtonType,
  VALIDATOR_PARAM_FIELD,
  packAiStyleValue,
} from './designer.service'

// --- Types ---

export enum UiFlowNavigationActionType {
  Navigate = 'navigate',
  OpenModal = 'open_modal',
}

export type UiFlowNavigationEntry = {
  source_button_key: string
  target_page_key: string
  action_type: UiFlowNavigationActionType
}

export type UiFlowLinkButton = {
  button_key: string
  button_type: UiFlowLinkButtonType
  form_controls?: string[]
}

export enum UiFlowAreaStatus {
  New = 'new',
  Edited = 'edited',
}

/** One edited/new top-level area of the grouped `edit_ui_flow` answer. */
export type UiFlowEditAreaResult = {
  key: string
  ci: string
  status: UiFlowAreaStatus
  // Per-key change report (add-only contract: removals never happen).
  changed?: { added?: string[]; edited?: string[] }
  components: AiUiTemplateElement[]
}

export type UiFlowNavigationDelta = {
  kept: UiFlowNavigationEntry[]
  added: UiFlowNavigationEntry[]
  removed: UiFlowNavigationEntry[]
}

/** The UI slice of the composed v8 answer for `generate_ui_flow` / `edit_ui_flow`. */
export type UiFlowGenerationOutcome = AiTemplateConfig & {
  navigation?: UiFlowNavigationEntry[]
  link_buttons?: UiFlowLinkButton[]
  /** Present only on grouped edit answers: the edited/new areas (edited-areas-only response). */
  areas?: UiFlowEditAreaResult[]
  /** Present only on grouped edit answers: the kept/added/removed navigation entries. */
  navigationDelta?: UiFlowNavigationDelta
}

/** One top-level area of the grouped DESIGNER_CONTEXT sent on `edit_ui_flow`. */
export type UiFlowGroupedArea = Partial<AiUiTemplateElement> & {
  id: number
  ci: string
  components: AiUiTemplateElement[]
}

/** The grouped DESIGNER_CONTEXT shape for `edit_ui_flow`. */
export type UiFlowGroupedContext = {
  interface: AiUiInterface
  areas: UiFlowGroupedArea[]
  navigation: UiFlowNavigationEntry[]
}

export type UiFlowPersistedNodeRef = { id: number; flowxUuid: string }

/** A draft tree and the persisted tree the server echoed back for it, ids and flowxUuids assigned. */
export type UiFlowDraftPair = {
  draft: TemplateConfigInterface
  persisted: TemplateConfigInterface
  startInModal?: boolean
}

/** Cross-root lookup built after persisting the generated trees. */
export type UiFlowKeyIndex = {
  /** AI element key (`k`) → persisted template-config id + flowxUuid. */
  byKey: Record<string, UiFlowPersistedNodeRef>
  /** Form-field key → flowxUuid of its nearest FORM ancestor. */
  formUuidByFieldKey: Record<string, string>
  /** Keys of elements living inside a MODAL (DISMISS is only valid there). */
  modalKeys: Set<string>
}

/** The compact DSL's "this element has no parent" marker, so a top-level root carries `pId: 0`. */
export const AI_NO_PARENT_ID = 0

export const AI_DRAFT_UUID_PREFIX = 'ai-draft-'

/**
 * Deterministic draft identities: negative ids so a draft never collides with a persisted one,
 * and prefixed uuids so a preview rebuilt on an unrelated store change keeps the same identities.
 */
export const createDraftIdentityGenerators = (): {
  nextId: () => number
  nextFlowxUuid: () => string
} => {
  let idSeq = 0
  let uuidSeq = 0
  return {
    nextId: () => --idSeq,
    nextFlowxUuid: () => `${AI_DRAFT_UUID_PREFIX}${++uuidSeq}`,
  }
}

export const deviceTypeFromPlatform = (platform: ProcessPlatform | undefined): DeviceType =>
  platform === ProcessPlatform.Mobile ? DeviceType.Mobile : DeviceType.Web

/** The compact answer indexed by `pId`, so a tree walk reads each parent's children once. */
const groupComponentsByParentId = (
  components: AiUiTemplateElement[]
): Record<string, AiUiTemplateElement[]> => _groupBy('pId', components)

/** First occurrence of each `k` wins; unkeyed components carry nothing to collide on and all stay. */
export const dedupeComponentsByKey = (components: AiUiTemplateElement[]): AiUiTemplateElement[] => {
  const seenKeys = new Set<string>()
  return components.filter((component) => {
    if (!component.k) {
      return true
    }
    if (seenKeys.has(component.k)) {
      return false
    }
    seenKeys.add(component.k)
    return true
  })
}

/** Human wording for a navigation area, used in prompts, menu labels and the chat summary. */
export const UI_FLOW_AREA_TYPE_LABELS: Partial<Record<ComponentIdentifierEnum, string>> = {
  [ComponentIdentifierEnum.PAGE]: 'page',
  [ComponentIdentifierEnum.TAB]: 'tab',
  [ComponentIdentifierEnum.ZONE]: 'zone',
  [ComponentIdentifierEnum.MODAL]: 'modal',
  [ComponentIdentifierEnum.TAB_BAR]: 'tab bar',
  [ComponentIdentifierEnum.STEPPER]: 'stepper',
  [ComponentIdentifierEnum.STEP]: 'step',
}

/** No root at all, or a selected root without children (the freshly-seeded-flow case). */
export const uiFlowCanvasHasNoContent = (
  selectedRoot: Pick<TemplateConfigInterface, 'templateConfig'> | null | undefined
): boolean => !selectedRoot?.templateConfig?.length

// --- Reverse serializer (TemplateConfig forest → compact AI format) ---

const IDENTIFIER_TO_AI_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(DesignerService.AiComponentIdentifierToComponentIdentifierEnum).map(
    ([code, identifier]) => [identifier, code]
  )
)

// Synthetic wrappers the compact DSL does not know: their children are lifted to the parent.
const FLATTENED_WRAPPER_IDENTIFIERS = new Set<string>([
  ComponentIdentifierEnum.TABLE_ROW,
  ComponentIdentifierEnum.COLLECTION_PROTOTYPE,
])

const flowxPropsOf = (
  node: TemplateConfigInterface,
  platform: string
): Record<string, unknown> =>
  (node.platformsDisplayOptions?.find((options) => options.platform === platform)?.flowxProps ??
    {}) as Record<string, unknown>

/** A component's prop buckets and style, resolved once per serialized node. */
type CompactView = { buckets: Record<string, unknown>[]; style: Record<string, any> }

// Props are read generic first and then per platform: a card's title, for one, is written
// per-platform, so reading only the generic options would report it as absent and the agent would
// answer without it. The style is read from web because that is where the mapper writes it.
const compactViewOf = (node: TemplateConfigInterface): CompactView => {
  const byPlatform = (node.platformsDisplayOptions ?? []).filter(
    (options) => options.platform !== SUPPORTED_PLATFORMS2.Generic
  )
  const preferred =
    byPlatform.find((options) => options.platform === SUPPORTED_PLATFORMS2.Web) ?? byPlatform[0]
  return {
    buckets: [
      flowxPropsOf(node, SUPPORTED_PLATFORMS2.Generic),
      ...byPlatform.map((options) => (options.flowxProps ?? {}) as Record<string, unknown>),
    ],
    style: (preferred?.style?.[DEFAULT_STYLE_KEY] ?? {}) as Record<string, any>,
  }
}

/** The first value the component carries for any of `props`. */
const compactProp = ({ buckets }: CompactView, ...props: string[]): unknown =>
  buckets
    .map((bucket) => props.map((prop) => bucket[prop]).find((value) => value != null))
    .find((value) => value != null)

// The label lives in a different flowxProp per component (see TemplateConfigEntity.withLabel).
const COMPACT_LABEL_PROPS = ['label', 'text', 'title', 'message']
const COMPACT_HELPER_TEXT_PROPS = ['subtitle', 'helperText', 'description']
const BOX_SIDES = ['Top', 'Right', 'Bottom', 'Left']
const FLEX_WRAP = 'wrap'
const FLEX_NOWRAP = 'nowrap'
const DEFAULT_STYLE_KEY = '_default'

const extractCompactLabel = (view: CompactView): string =>
  String(compactProp(view, ...COMPACT_LABEL_PROPS) ?? '')

// A field is only omitted when the persisted node genuinely has no value for it. `0` and
// `'nowrap'` are values the agent set deliberately, and dropping them as falsy is how zero
// padding and a zero gap came back as whatever the mapper's default happened to be.
const present = <T>(value: T): boolean => value != null && value !== ''

const boxSides = (
  style: Record<string, any>,
  prefix: 'padding' | 'margin'
): string | undefined => {
  const sides = BOX_SIDES.map((side) => style[`${prefix}${side}`])
  return sides.some(present) ? sides.map((side) => side ?? 0).join(' ') : undefined
}

const emit = (field: keyof AiUiTemplateElement, value: unknown): Partial<AiUiTemplateElement> =>
  (present(value) ? { [field]: value } : {}) as Partial<AiUiTemplateElement>

const emitAll = (
  style: Record<string, any>,
  fields: readonly (readonly [keyof AiUiTemplateElement, string])[],
  read: (value: any) => unknown = (value) => value
): Partial<AiUiTemplateElement> =>
  Object.fromEntries(
    fields.map(([field, key]) => [field, read(style[key])]).filter(([, value]) => present(value))
  ) as Partial<AiUiTemplateElement>

const PASSTHROUGH_STYLE_FIELDS = [
  ['wt', 'widthType'],
  ['w', 'width'],
  ['ht', 'heightType'],
  ['h', 'height'],
] as const
// Colours persist as theme resources (`{ value }`); the agent's schema wants the string.
const COLOUR_STYLE_FIELDS = [
  ['bgCol', 'backgroundColor'],
  ['fgCol', 'labelColor'],
] as const
const themeResourceValue = (resource: { value?: unknown } | undefined): string | undefined =>
  present(resource?.value) ? String(resource.value) : undefined

/**
 * A persisted component back in the agent's own vocabulary — `TemplateConfiguration` in the
 * agent's schema, which is both what it reads and what it writes.
 *
 * This has to stay the exact inverse of what `mapAiUiToTemplateConfig` persists. The edit graph
 * applies a field-level diff on top of the components we send, so a field missing here is missing
 * from the answer, and anything rebuilt from that answer loses it. The round-trip spec is what
 * holds the two in step; extend it before extending this.
 */
const compactStyleFields = ({ style }: CompactView): Partial<AiUiTemplateElement> => {
  const flex = style.flexLayout ?? {}
  const grid = style.gridLayout ?? {}
  const [fxLayout, wrap] = String(flex.fxLayout ?? '').split(' ')
  const [justify, align] = String(flex.fxLayoutAlign ?? '').split(' ')
  const hasLayoutType = present(style.layoutType)
  const isGrid = style.layoutType === LayoutType.Grid

  return {
    ...(hasLayoutType ? { lt: isGrid ? LayoutType.Grid : LayoutType.Linear } : {}),
    ...emit('dir', fxLayout),
    // The mapper only ever appends ' wrap'; anything else the agent sent was 'nowrap'.
    ...(hasLayoutType && !isGrid ? { wp: wrap === FLEX_WRAP ? FLEX_WRAP : FLEX_NOWRAP } : {}),
    ...emit('js', justify),
    ...emit('al', align),
    // Grid keeps its gaps on both axes; flex has one gap, which the mapper reads from gapv first.
    ...(isGrid
      ? {
          ...emit('cl', grid.columns),
          ...emit('gaph', grid.columnGap),
          ...emit('gapv', grid.rowGap),
        }
      : emit('gapv', flex.fxLayoutGap)),
    ...emit('cspan', style.gridChild?.colSpan),
    ...emit('pd', boxSides(style, 'padding')),
    ...emit('mg', boxSides(style, 'margin')),
    ...emitAll(style, PASSTHROUGH_STYLE_FIELDS),
    ...emitAll(style, COLOUR_STYLE_FIELDS, themeResourceValue),
  }
}

const compactTypography = ({ style }: CompactView): string => {
  const typography = String(style.labelStyle?.reference ?? '')
  const [token, color = ''] = typography.replace(/^typography@\w+\//, '').split('#')
  return token ? packAiStyleValue(color, token) : ''
}

const compactFixedSize = ({ style }: CompactView): string =>
  style.widthType === DimensionTypes.Fixed && present(style.width) && present(style.height)
    ? packAiStyleValue(String(style.width), String(style.height))
    : ''

/**
 * `st` carries a different thing per component, and the mapper consumes it strictly by
 * identifier — the button variant, the input type, the message severity, a text's typography
 * reference, an image's fixed `w//h` — so the inverse dispatches on the identifier too, rather
 * than probing fields a component of another type might happen to carry.
 */
const COMPACT_STYLE_VALUE_READERS: Record<string, (view: CompactView) => string> = {
  [ComponentIdentifierEnum.BUTTON]: (view) => String(compactProp(view, 'type') ?? ''),
  [ComponentIdentifierEnum.MESSAGE]: (view) => String(compactProp(view, 'messageType') ?? ''),
  [FormFieldsEnum.INPUT]: (view) => String(compactProp(view, 'inputType') ?? ''),
  [ComponentIdentifierEnum.TEXT]: compactTypography,
  [ComponentIdentifierEnum.LINK]: compactTypography,
  [ComponentIdentifierEnum.IMAGE]: compactFixedSize,
}

/** The compact content and behaviour fields: helper text, placeholder, rules, props, validators. */
const compactContentFields = (
  view: CompactView,
  node: TemplateConfigInterface
): Partial<AiUiTemplateElement> => {
  const st = COMPACT_STYLE_VALUE_READERS[node.componentIdentifier]?.(view) ?? ''
  const ex = compactProp(view, 'placeholder')
  const ds = compactProp(view, ...COMPACT_HELPER_TEXT_PROPS)
  const hideRules = compactProp(view, 'hide')
  const disableRules = compactProp(view, 'disabled')
  // Persisted validators name their rule on `name`; the agent's schema calls it `type`. One with
  // neither is nothing the agent can act on, so it is not sent.
  const validators = (node.validators ?? [])
    .map((validator) => ({
      validator,
      type: (validator as { name?: string }).name ?? validator.type,
    }))
    .filter(({ type }) => !!type)
    .map(({ validator, type }): AiValidator => {
      const param = (validator as { params?: unknown[] }).params?.[0]
      const paramField = VALIDATOR_PARAM_FIELD[type as keyof typeof VALIDATOR_PARAM_FIELD]
      return {
        type,
        ...(validator.message ? { message: validator.message } : {}),
        ...(paramField && param != null ? { [paramField]: param } : {}),
      }
    })

  return {
    ...(st ? { st } : {}),
    ...(present(ex) ? { ex: String(ex) } : {}),
    ...(present(ds) ? { ds: String(ds) } : {}),
    ...(present(hideRules) ? { hideRules: String(hideRules) } : {}),
    ...(present(disableRules) ? { disableRules: String(disableRules) } : {}),
    ...(validators.length ? { validators } : {}),
  }
}

/**
 * The keys of areas the compact serializer has to leave out, because their component has no code
 * in the agent's vocabulary — a STEPPER root, which the platform allows but `NavigationComponent`
 * does not cover. The navigation into such an area is still sent, so callers need to know which
 * targets the agent was never actually shown.
 *
 * TODO(MT-3621): remove once the agent's vocabulary covers stepper areas — the serializer will
 * emit them like any other root and nothing will be unreachable.
 */
export const collectUnserializableUiFlowAreaKeys = (
  roots: TemplateConfigInterface[]
): Set<string> =>
  new Set(
    (roots ?? [])
      .filter((root) => root && !IDENTIFIER_TO_AI_CODE[root.componentIdentifier])
      .map((root) => templateConfigAiIdentity(root) ?? '')
      .filter(Boolean)
  )

/**
 * Serializes a UI Flow forest to the GROUPED compact format sent as DESIGNER_CONTEXT for
 * `edit_ui_flow`: one entry per top-level navigation area (the root's own compact fields plus
 * its flat `components` subtree), a global `navigation` array reconstructed from the flow's
 * persisted click handlers, and the target platform on `interface.ty`. Ids are unique across
 * the whole flow.
 */
export const serializeUiFlowToGrouped = (
  roots: TemplateConfigInterface[],
  options: {
    flowName?: string
    platform?: AiPlatformCode
    navigation?: UiFlowNavigationEntry[]
  } = {}
): UiFlowGroupedContext => {
  const areas: UiFlowGroupedArea[] = []
  let seq = 0

  const compactFieldsOf = (
    node: TemplateConfigInterface,
    id: number,
    pId: number
  ): AiUiTemplateElement => {
    const view = compactViewOf(node)
    const k = templateConfigAiIdentity(node) ?? ''
    const lb = extractCompactLabel(view)
    return {
      id,
      pId,
      ci: IDENTIFIER_TO_AI_CODE[node.componentIdentifier],
      ...(k ? { k } : {}),
      ...(lb ? { lb } : {}),
      ...compactStyleFields(view),
      ...compactContentFields(view, node),
    } as AiUiTemplateElement
  }

  const serializeNode = (
    node: TemplateConfigInterface,
    pId: number,
    sink: AiUiTemplateElement[]
  ): void => {
    if (!node) {
      return
    }

    if (FLATTENED_WRAPPER_IDENTIFIERS.has(node.componentIdentifier)) {
      const lifted = _sortBy('order', node.templateConfig ?? [])
      lifted.forEach((child) => serializeNode(child, pId, sink))
      return
    }

    if (!IDENTIFIER_TO_AI_CODE[node.componentIdentifier]) {
      return
    }

    const id = ++seq
    sink.push(compactFieldsOf(node, id, pId))

    const children = _sortBy('order', node.templateConfig ?? [])
    children.forEach((child) => serializeNode(child, id, sink))
  }

  ;(roots ?? [])
    .filter((root) => root && IDENTIFIER_TO_AI_CODE[root.componentIdentifier])
    .forEach((root) => {
      const id = ++seq
      const components: AiUiTemplateElement[] = []
      const children = _sortBy('order', root.templateConfig ?? [])
      children.forEach((child) => serializeNode(child, id, components))

      areas.push({ ...compactFieldsOf(root, id, AI_NO_PARENT_ID), components })
    })

  return {
    interface: {
      it: options.flowName ?? '',
      ty: options.platform ?? AI_PLATFORM_CODE.Web,
      ds: '',
    },
    areas,
    navigation: options.navigation ?? [],
  }
}

// --- Navigation reconstruction (persisted click handlers → compact entries) ---

/** Depth-first visit of every node under the given roots, null entries skipped. */
export const walkTemplateConfigTrees = (
  roots: readonly (TemplateConfigInterface | null | undefined)[] | null | undefined,
  visit: (node: TemplateConfigInterface) => void
): void =>
  (roots ?? []).forEach((node) => {
    if (!node) {
      return
    }
    visit(node)
    walkTemplateConfigTrees(node.templateConfig, visit)
  })

/** DFS collection of the flow's BUTTON nodes (the only handler sources the AI wire carries). */
export const collectUiFlowButtonNodes = (
  roots: TemplateConfigInterface[]
): TemplateConfigInterface[] => {
  const buttons: TemplateConfigInterface[] = []
  walkTemplateConfigTrees(roots, (node) => {
    if (node.componentIdentifier === ComponentIdentifierEnum.BUTTON && node.id != null) {
      buttons.push(node)
    }
  })

  return buttons
}

/**
 * Rebuilds the compact `navigation` array from the flow's persisted NAVIGATE_TO handlers:
 * source = the handler's button (by key/customIdentifier), target resolved from
 * `params.destination.uiTemplateFlowxUuid`, and `action_type` is `open_modal` iff the target
 * is a MODAL. Handlers whose source has no key or whose target no longer exists are skipped.
 */
export const buildUiFlowNavigationFromHandlers = (
  roots: TemplateConfigInterface[],
  handlersByButton: { button: TemplateConfigInterface; handlers: EventHandler[] }[]
): UiFlowNavigationEntry[] => {
  const nodeByUuid: Record<string, TemplateConfigInterface> = {}
  walkTemplateConfigTrees(roots, (node) => {
    if (node.flowxUuid) {
      nodeByUuid[node.flowxUuid] = node
    }
  })

  const entries: UiFlowNavigationEntry[] = []
  handlersByButton.forEach(({ button, handlers }) => {
    const sourceKey = templateConfigAiIdentity(button)
    if (!sourceKey) {
      return
    }
    const navigateHandlers = (handlers ?? []).filter(
      (handler) => handler.type === EventHandlerActionType.NAVIGATE_TO
    )
    navigateHandlers
      .forEach((handler) => {
        const destinationUuid = (
          handler.params as { destination?: { uiTemplateFlowxUuid?: string } } | undefined
        )?.destination?.uiTemplateFlowxUuid
        const target = destinationUuid ? nodeByUuid[destinationUuid] : undefined
        const targetKey = target ? templateConfigAiIdentity(target) : undefined
        if (!targetKey) {
          return
        }
        entries.push({
          source_button_key: sourceKey,
          target_page_key: targetKey,
          action_type:
            target.componentIdentifier === ComponentIdentifierEnum.MODAL
              ? UiFlowNavigationActionType.OpenModal
              : UiFlowNavigationActionType.Navigate,
        })
      })
  })

  return entries
}

// --- Grouped edit draft preview ---

/**
 * The canvas preview for a grouped `edit_ui_flow` answer: the edited/new area matching the
 * currently selected root (falling back to the first area), converted to a draft
 * TemplateConfig tree that REPLACES the canvas in place — never appended into the existing
 * page like a create draft.
 */
/**
 * The persisted root a grouped area writes into. The area's `status` is the agent's claim, not a
 * fact: an area reported `new` whose key already names a root is edited, since creating a second
 * root under the same key would leave navigation resolving against whichever the walk hit last.
 */
export const resolveUiFlowAreaExistingRoot = (
  area: UiFlowEditAreaResult,
  roots: TemplateConfigInterface[]
): TemplateConfigInterface | null => {
  const existingRoot = findUiFlowRootByAreaKey(roots, area.key)
  if (existingRoot && area.status === UiFlowAreaStatus.New) {
    console.warn(`Area "${area.key}" was reported new but already exists — editing it instead.`)
  }
  return existingRoot
}

/**
 * The sanitize-and-build every consumer of a grouped area shares, so a preview cannot offer a
 * component the keep would silently drop: the existing-root resolution and the sanitizer's
 * root-widening to that root's type live here and nowhere else.
 */
export const buildUiFlowAreaDraftTrees = (
  area: UiFlowEditAreaResult,
  roots: TemplateConfigInterface[],
  aiInterface: UiFlowGenerationOutcome['interface'],
  deviceType: DeviceType,
  idGenerator: () => number,
  flowxUuidGenerator: () => string
): { existingRoot: TemplateConfigInterface | null; trees: TemplateConfigInterface[] } => {
  const existingRoot = resolveUiFlowAreaExistingRoot(area, roots)
  const components = sanitizeUiFlowAiComponents(
    area.components,
    deviceType,
    existingRoot ? [existingRoot.componentIdentifier] : []
  )
  return {
    existingRoot,
    trees: buildUiFlowTemplateTrees(components, aiInterface, idGenerator, flowxUuidGenerator),
  }
}

export const buildUiFlowEditAreaPreview = (
  outcome: UiFlowGenerationOutcome,
  selectedRoot: TemplateConfigInterface | null | undefined,
  idGenerator: () => number,
  flowxUuidGenerator: () => string,
  deviceType: DeviceType
): TemplateConfigInterface | null => {
  const areas = outcome.areas ?? []
  if (!areas.length) {
    return null
  }

  // A grouped answer holds only the touched areas, so an untouched root matches nothing: preview
  // nothing and let the canvas show that root's real content instead of another area's draft.
  const selectedKey = selectedRoot ? templateConfigAiIdentity(selectedRoot) : null
  const area = selectedKey ? areas.find((candidate) => candidate.key === selectedKey) : areas[0]
  if (!area?.components?.length) {
    return null
  }

  try {
    const [tree] = buildUiFlowAreaDraftTrees(
      area,
      selectedRoot ? [selectedRoot] : [],
      outcome.interface,
      deviceType,
      idGenerator,
      flowxUuidGenerator
    ).trees
    return tree?.componentIdentifier ? ({ ...tree, draft: true } as TemplateConfigInterface) : null
  } catch (error) {
    console.error('Failed to build ui-flow edit preview:', error)
    return null
  }
}

// --- Existing-flow lookups ---

/** The root tree of the flow whose subtree contains the given template-config id. */
export const findOwningUiFlowRoot = (
  roots: TemplateConfigInterface[],
  id: number
): TemplateConfigInterface | null =>
  (roots ?? []).find((root) => !!findUiFlowNodeById([root], id)) ?? null

/** The root matched by the grouped answer's area `key` (AI identity: key or customIdentifier). */
export const findUiFlowRootByAreaKey = (
  roots: TemplateConfigInterface[],
  areaKey: string
): TemplateConfigInterface | null =>
  (roots ?? []).find((root) => templateConfigAiIdentity(root) === areaKey) ?? null

/**
 * Whether `nodeId` sits inside a MODAL within `root` — the node itself counting. Drives the
 * DISMISS handler a cancel button only gets when it lives in a modal.
 */
export const isInsideModal = (
  root: TemplateConfigInterface,
  nodeId: number,
  seenModal = false
): boolean => {
  if (!root) {
    return false
  }
  const inModal = seenModal || root.componentIdentifier === ComponentIdentifierEnum.MODAL
  if (root.id === nodeId) {
    return inModal
  }
  return (root.templateConfig ?? []).some((child) => isInsideModal(child, nodeId, inModal))
}

/** Depth-first lookup by AI identity (key or customIdentifier) anywhere in a tree. */
export const findUiFlowNodeByKey = (
  node: TemplateConfigInterface,
  key: string
): TemplateConfigInterface | null =>
  key ? findUiFlowNode([node], (candidate) => templateConfigAiIdentity(candidate) === key) : null

/** Every AI identity within a subtree, the node itself included. */
export const collectUiFlowSubtreeKeys = (node: TemplateConfigInterface): Set<string> => {
  const keys = new Set<string>()
  walkTemplateConfigTrees([node], (current) => {
    const key = templateConfigAiIdentity(current)
    if (key) {
      keys.add(key)
    }
  })
  return keys
}

/** Depth-first search across the flow's root trees for the first node matching `predicate`. */
export const findUiFlowNode = (
  roots: TemplateConfigInterface[],
  predicate: (node: TemplateConfigInterface) => boolean
): TemplateConfigInterface | null =>
  (roots ?? []).reduce<TemplateConfigInterface | null>(
    (found, root) => found ?? findTemplateConfigDescendantWith(root, predicate) ?? null,
    null
  )

export const findUiFlowNodeById = (
  roots: TemplateConfigInterface[],
  id: number
): TemplateConfigInterface | null => findUiFlowNode(roots, (node) => node.id === id)

/**
 * key/customIdentifier → { id, flowxUuid } over the EXISTING flow trees, so scoped-edit
 * navigation can target areas outside the edited subtree (unresolvable targets are skipped
 * by the handler synthesis).
 */
export const buildExistingUiFlowKeyRefs = (
  roots: TemplateConfigInterface[]
): Record<string, UiFlowPersistedNodeRef> => {
  const refs: Record<string, UiFlowPersistedNodeRef> = {}
  walkTemplateConfigTrees(roots, (node) => {
    const aiKey = templateConfigAiIdentity(node)
    if (aiKey && node.id != null && node.flowxUuid) {
      refs[aiKey] = { id: node.id, flowxUuid: node.flowxUuid }
    }
  })
  return refs
}

// --- Changed-subtree apply planning ---

export type UiFlowChangedSubtreePlan = {
  /** Persisted nodes whose subtree is being replaced (deleted before the create). */
  deletes: TemplateConfigInterface[]
  /** Draft subtrees to create, each under an UNTOUCHED persisted parent. */
  creates: { draft: TemplateConfigInterface; parentId: number; order?: number }[]
}

/**
 * Turns the agent's per-area change report into the minimal set of subtree operations:
 * only the reported added/edited components (collapsed to the topmost changed ancestors)
 * are replaced; everything else in the area keeps its persisted node — styling, manual
 * tweaks, and click handlers included. Removals never occur (add-only edit contract).
 *
 * Returns null when the plan cannot be built safely (a reported key missing from the
 * draft, or a changed subtree whose parent cannot be located in the persisted area) —
 * callers fall back to the legacy full-area replace.
 */
export const planUiFlowChangedSubtrees = (
  changed: { added?: string[]; edited?: string[] } | undefined,
  areaDraftTree: TemplateConfigInterface,
  existingRoot: TemplateConfigInterface
): UiFlowChangedSubtreePlan | null => {
  if (!changed) {
    return null
  }
  const changedKeys = new Set([...(changed.added ?? []), ...(changed.edited ?? [])])
  if (!changedKeys.size) {
    return { deletes: [], creates: [] }
  }

  const draftKeyOf = (node: TemplateConfigInterface): string => templateConfigAiIdentity(node) ?? ''

  const draftInfo = new Map<
    string,
    { node: TemplateConfigInterface; parent: TemplateConfigInterface | null }
  >()
  const orderedDraftKeys: string[] = []
  // Every node, keyed or not. `k` is optional on structural components, and the mapper adds
  // unkeyed TABLE_ROW / COLLECTION_PROTOTYPE wrappers of its own, so walking parents by key
  // would step straight over them — losing a node's real parent and its changed ancestors.
  const draftParents = new Map<TemplateConfigInterface, TemplateConfigInterface | null>()
  const walkDraft = (
    node: TemplateConfigInterface,
    parent: TemplateConfigInterface | null
  ): void => {
    draftParents.set(node, parent)
    const key = draftKeyOf(node)
    if (key && !draftInfo.has(key)) {
      draftInfo.set(key, { node, parent })
      orderedDraftKeys.push(key)
    }
    node.templateConfig?.forEach((child) => walkDraft(child, node))
  }
  walkDraft(areaDraftTree, null)

  const persistedByKey = new Map<string, TemplateConfigInterface>()
  walkTemplateConfigTrees([existingRoot], (node) => {
    const key = draftKeyOf(node)
    if (key && !persistedByKey.has(key)) {
      persistedByKey.set(key, node)
    }
  })

  const isInsideChangedAncestor = (key: string): boolean => {
    let parent = draftInfo.get(key)?.parent ?? null
    while (parent) {
      if (changedKeys.has(draftKeyOf(parent))) {
        return true
      }
      parent = draftParents.get(parent) ?? null
    }
    return false
  }

  /**
   * The persisted node a changed subtree should be created under. Only a keyed parent can be
   * matched: an unkeyed one has no identifiable twin in the persisted area, so recreating the
   * subtree would duplicate it rather than replace it, and creating it further up would tear the
   * node out of its container. Null instead, which sends the caller to the full-area replace.
   */
  const persistedParentFor = (
    node: TemplateConfigInterface
  ): TemplateConfigInterface | undefined => {
    const parent = draftParents.get(node)
    if (!parent) {
      return undefined
    }
    const parentKey = draftKeyOf(parent)
    return parentKey ? persistedByKey.get(parentKey) : undefined
  }

  const areaKey = draftKeyOf(areaDraftTree)
  const deletes: TemplateConfigInterface[] = []
  const creates: UiFlowChangedSubtreePlan['creates'] = []
  const topmostKeys = orderedDraftKeys.filter(
    (key) => changedKeys.has(key) && key !== areaKey && !isInsideChangedAncestor(key)
  )
  for (const key of topmostKeys) {
    const info = draftInfo.get(key)
    if (!info) {
      return null
    }
    const parentNode = info.parent ? persistedParentFor(info.node) : existingRoot
    if (parentNode?.id == null) {
      return null
    }
    const existingNode = persistedByKey.get(key)
    if (existingNode) {
      deletes.push(existingNode)
    }
    creates.push({ draft: info.node, parentId: parentNode.id, order: info.node.order })
  }
  // Reported keys the draft does not actually contain make the plan untrustworthy.
  for (const key of changedKeys) {
    if (key !== areaKey && !draftInfo.has(key)) {
      return null
    }
  }
  return { deletes, creates }
}

// --- Outcome normalization ---

/**
 * Normalizes the parsed MAIN outcome of a ui-flow generation to a flat
 * `{ interface, components, navigation, link_buttons }` shape — the composed v8 answer may
 * nest the UI slice under `interface`, or arrive grouped per area on edits.
 */
export const normalizeUiFlowOutcome = (parsed: unknown): UiFlowGenerationOutcome | null => {
  if (!parsed || typeof parsed !== 'object') {
    return null
  }

  const candidate = parsed as Record<string, unknown>

  // Grouped edit answer: first area's components become the preview slice, delta kept for apply.
  if (Array.isArray(candidate.areas)) {
    const areas = candidate.areas as UiFlowEditAreaResult[]
    const delta = (candidate.navigation ?? {}) as Partial<UiFlowNavigationDelta>
    return {
      interface: candidate.interface,
      components: areas[0]?.components ?? [],
      areas,
      navigation: delta.added ?? [],
      navigationDelta: {
        kept: delta.kept ?? [],
        added: delta.added ?? [],
        removed: delta.removed ?? [],
      },
      link_buttons: candidate.link_buttons,
    } as UiFlowGenerationOutcome
  }

  if (Array.isArray(candidate.components)) {
    return candidate as unknown as UiFlowGenerationOutcome
  }

  const nested = candidate.interface as Record<string, unknown> | undefined
  if (nested && typeof nested === 'object' && Array.isArray(nested.components)) {
    return {
      ...(nested as unknown as UiFlowGenerationOutcome),
      link_buttons: (candidate.link_buttons ??
        nested.link_buttons) as UiFlowGenerationOutcome['link_buttons'],
    }
  }

  return null
}

// --- Navigation-area summary rows (chat) ---

export type UiFlowAreaSummaryRow = {
  key: string
  label: string
  /** The mapped component identifier — also the flx-icon name in the navigator icon set. */
  componentIdentifier: string
  typeLabel: string
  depth: number
}

/**
 * Flattens a flow answer into the chat summary's navigation-area rows: only area nodes
 * (page / zone / tab bar / tab / stepper / step / modal) become rows, in document order, with
 * `depth` counting AREA ancestors only — intermediate containers do not indent. Rows are flat
 * on purpose: the design zebra-stripes sibling rows. Depth-0 rows are the flow's roots (the
 * areas the header dropdown can switch between).
 */
export const buildUiFlowAreaSummaryRows = (
  outcome: UiFlowGenerationOutcome | null | undefined
): UiFlowAreaSummaryRow[] => {
  const componentLists = outcome?.areas?.length
    ? outcome.areas.map((area) => area.components ?? [])
    : [outcome?.components ?? []]

  const visit = (
    childrenByParentId: Record<string, AiUiTemplateElement[]>,
    pId: number,
    depth: number,
    seen: Set<number>
  ): UiFlowAreaSummaryRow[] =>
    (childrenByParentId[pId] ?? [])
      .filter((component) => !seen.has(component.id))
      .flatMap((component) => {
        seen.add(component.id)
        const identifier = toComponentIdentifier(component.ci)
        const typeLabel = identifier ? UI_FLOW_AREA_TYPE_LABELS[identifier] : undefined
        return typeLabel
          ? [
              {
                key: component.k ?? '',
                label: component.lb || component.k || typeLabel,
                componentIdentifier: identifier as string,
                typeLabel,
                depth,
              },
              ...visit(childrenByParentId, component.id, depth + 1, seen),
            ]
          : visit(childrenByParentId, component.id, depth, seen)
      })

  return componentLists.flatMap((components) =>
    visit(groupComponentsByParentId(components), AI_NO_PARENT_ID, 0, new Set<number>())
  )
}

// --- Validation guard (sanitizer) ---

const toComponentIdentifier = (
  ci: string | undefined
): ComponentIdentifierEnum | FormFieldsEnum | undefined =>
  ci ? DesignerService.AiComponentIdentifierToComponentIdentifierEnum[ci] : undefined

const FORM_FIELD_IDENTIFIERS = new Set<string>(Object.values(FormFieldsEnum))

const isAiExpressible = (identifier: string): boolean => !!IDENTIFIER_TO_AI_CODE[identifier]

// The platform's flow roots that have a code in the agent's vocabulary (a STEPPER has none).
const UI_FLOW_ALLOWED_ROOTS: ReadonlySet<string> = new Set(
  allowedUiFlowRootComponents.filter(isAiExpressible)
)

// Dropped as whole subtrees when the flow targets a mobile platform.
const UI_FLOW_WEB_ONLY: ReadonlySet<string> = new Set<string>(webOnlyComponents)

const UI_FLOW_ALLOWED_ROOTS_MOBILE: ReadonlySet<string> = new Set(
  [...UI_FLOW_ALLOWED_ROOTS].filter((root) => !UI_FLOW_WEB_ONLY.has(root))
)

type NestingRules = Record<string, string[]>

const mapRules = (rules: NestingRules, rewrite: (children: string[]) => string[]): NestingRules =>
  Object.fromEntries(Object.entries(rules).map(([parent, children]) => [parent, rewrite(children)]))

// The sanitizer runs on the compact forest, where TABLE children are TABLE_CELLs and COLLECTION
// children are the prototype's: the DSL has no name for the TABLE_ROW / COLLECTION_PROTOTYPE
// wrappers, so their children are lifted to the wrapper's parent — the same lift the serializer
// performs. Without it TABLE and COLLECTION would narrow to an empty list.
const liftFlattenedWrappers = (rules: NestingRules): NestingRules =>
  mapRules(rules, (children) =>
    children.flatMap((child) =>
      FLATTENED_WRAPPER_IDENTIFIERS.has(child) ? (rules[child] ?? []) : [child]
    )
  )

// The designer's own UI Flow nesting rules, per device type, narrowed to what the agent can emit.
const allowedChildrenFor = _memoize(
  (deviceType: DeviceType): NestingRules =>
    mapRules(
      liftFlattenedWrappers(allowedChildComponents(UiDesignerContext.UiFlow, deviceType)),
      (children) => children.filter(isAiExpressible)
    )
)

const allowedRootsFor = (deviceType: DeviceType): ReadonlySet<string> =>
  deviceType === DeviceType.Mobile ? UI_FLOW_ALLOWED_ROOTS_MOBILE : UI_FLOW_ALLOWED_ROOTS

export type UiFlowRepairLogEntry = {
  key: string
  label: string
  componentIdentifier: string
  reason: 'unknown-component' | 'web-only-on-mobile' | 'not-allowed-root' | 'not-allowed-child'
  parentIdentifier?: string
}

/**
 * Keeps only components that form a valid UI Flow forest for the target device type;
 * dropping a component drops its whole subtree, and each drop is RECORDED (the spec's repair
 * log — drop-only until a repair-first pass exists): one entry per discarded subtree ROOT,
 * descendants are implied. `extraAllowedRoots` widens the root rule for scoped area edits,
 * where the answer is rooted at the edited area itself.
 */
export const sanitizeUiFlowAiComponentsWithLog = (
  components: AiUiTemplateElement[],
  deviceType: DeviceType = DeviceType.Web,
  extraAllowedRoots: readonly string[] = []
): { components: AiUiTemplateElement[]; dropped: UiFlowRepairLogEntry[] } => {
  if (!components?.length) {
    return { components: [], dropped: [] }
  }

  const allowedChildrenMap = allowedChildrenFor(deviceType)
  const allowedRoots = new Set([...allowedRootsFor(deviceType), ...extraAllowedRoots])
  const kept = new Set<AiUiTemplateElement>()
  const dropped: UiFlowRepairLogEntry[] = []
  const childrenByParentId = groupComponentsByParentId(components)

  const mappabilityReason = (
    element: AiUiTemplateElement
  ): UiFlowRepairLogEntry['reason'] | undefined => {
    const identifier = toComponentIdentifier(element.ci)
    // Id 0 is the "no parent" marker, so a component owning it would adopt every other root and
    // send the tree builder into infinite recursion.
    if (element.id === AI_NO_PARENT_ID) {
      return 'unknown-component'
    }
    if (!identifier || identifier === ComponentIdentifierEnum.CHAT) {
      return 'unknown-component'
    }
    if (deviceType === DeviceType.Mobile && UI_FLOW_WEB_ONLY.has(identifier)) {
      return 'web-only-on-mobile'
    }
    return undefined
  }

  const toLogEntry = (
    element: AiUiTemplateElement,
    reason: UiFlowRepairLogEntry['reason'],
    parentIdentifier?: string
  ): UiFlowRepairLogEntry => ({
    key: element.k ?? '',
    label: element.lb || element.k || element.ci || '',
    componentIdentifier: toComponentIdentifier(element.ci) ?? element.ci ?? '',
    reason,
    ...(parentIdentifier ? { parentIdentifier } : {}),
  })

  const keepChildrenOf = (parent: AiUiTemplateElement): void => {
    const parentIdentifier = toComponentIdentifier(parent.ci) ?? ''
    const allowedChildren = allowedChildrenMap[parentIdentifier] ?? []

    ;(childrenByParentId[parent.id] ?? [])
      .filter((child) => !kept.has(child))
      .forEach((child) => {
        const reason =
          mappabilityReason(child) ??
          (allowedChildren.includes(toComponentIdentifier(child.ci) ?? '')
            ? undefined
            : 'not-allowed-child')
        if (reason) {
          dropped.push(toLogEntry(child, reason, parentIdentifier))
        } else {
          kept.add(child)
          keepChildrenOf(child)
        }
      })
  }

  ;(childrenByParentId[AI_NO_PARENT_ID] ?? []).forEach((root) => {
    const reason =
      mappabilityReason(root) ??
      (allowedRoots.has(toComponentIdentifier(root.ci) ?? '') ? undefined : 'not-allowed-root')
    if (reason) {
      dropped.push(toLogEntry(root, reason))
    } else {
      kept.add(root)
      keepChildrenOf(root)
    }
  })

  // Kept by object, not by id: a malformed answer can repeat an id, and filtering by value would
  // re-admit the twin that was just dropped.
  return { components: components.filter((component) => kept.has(component)), dropped }
}

export const sanitizeUiFlowAiComponents = (
  components: AiUiTemplateElement[],
  deviceType: DeviceType = DeviceType.Web,
  extraAllowedRoots: readonly string[] = []
): AiUiTemplateElement[] =>
  sanitizeUiFlowAiComponentsWithLog(components, deviceType, extraAllowedRoots).components

/**
 * Sanitizes an answer's component list with the root rule widened to the answer's OWN roots:
 * the child rules still apply (they are what drops e.g. a form field placed directly in a
 * container), but the roots are accepted as-is. For a scoped edit the answer is rooted at the
 * edited area (possibly a TAB or MODAL — never a legal flow root), and the apply path widens
 * the root rule per area; this mirrors that for consumers that only need the child pruning —
 * e.g. the data-model draft, which must see exactly the components the apply will keep.
 */
export const sanitizeUiFlowAnswerAiComponents = (
  components: AiUiTemplateElement[],
  deviceType: DeviceType = DeviceType.Web
): AiUiTemplateElement[] =>
  sanitizeUiFlowAiComponents(
    components,
    deviceType,
    (components ?? [])
      .filter((component) => component.pId === AI_NO_PARENT_ID)
      .map((component) => toComponentIdentifier(component.ci) ?? '')
  )

// --- Compact forest → TemplateConfig trees ---

const collectSubtree = (
  childrenByParentId: Record<string, AiUiTemplateElement[]>,
  root: AiUiTemplateElement,
  seen: Set<number> = new Set([root.id])
): AiUiTemplateElement[] => {
  const children = (childrenByParentId[root.id] ?? []).filter(
    (component) => !seen.has(component.id)
  )
  children.forEach((child) => seen.add(child.id))
  return [root, ...children.flatMap((child) => collectSubtree(childrenByParentId, child, seen))]
}

// A `k` on a structural component is its identity (used by navigation targets), not a
// data-model binding: keep `key` only on form fields, move it to `customIdentifier` elsewhere.
const relocateNonFormKeys = (node: TemplateConfigInterface): TemplateConfigInterface => {
  const isFormField = FORM_FIELD_IDENTIFIERS.has(node.componentIdentifier)
  const { key, ...rest } = node

  return {
    ...(isFormField || !key ? node : { ...rest, customIdentifier: node.customIdentifier || key }),
    templateConfig: node.templateConfig?.map(relocateNonFormKeys),
  } as TemplateConfigInterface
}

/**
 * Converts a sanitized compact forest into one nested `TemplateConfigInterface` tree per root
 * (a UI Flow is a forest: each PAGE / ZONE / TAB_BAR root is persisted separately).
 */
export const buildUiFlowTemplateTrees = (
  components: AiUiTemplateElement[],
  aiInterface: AiUiInterface,
  idGenerator: () => number,
  flowxUuidGenerator: () => string
): TemplateConfigInterface[] => {
  const childrenByParentId = groupComponentsByParentId(components)
  return (childrenByParentId[AI_NO_PARENT_ID] ?? [])
    .map(
      (root) =>
        DesignerService.mapAiUiToTemplateConfig(idGenerator, flowxUuidGenerator, {
          interface: aiInterface,
          components: collectSubtree(childrenByParentId, root),
        }) as TemplateConfigInterface
    )
    .filter((tree) => !!tree?.componentIdentifier)
    .map(relocateNonFormKeys)
}

// --- AI key → persisted id/flowxUuid index ---

/**
 * Walks the draft and persisted trees in parallel (children match by index — the server echoes
 * the posted tree back with ids/flowxUuids assigned) and records, per AI element key: the
 * persisted `{ id, flowxUuid }`, the nearest FORM ancestor's flowxUuid (for `formsToSubmit`)
 * and whether the element lives inside a MODAL. `startInModal` seeds the modal flag for scoped
 * edits applied inside an existing MODAL area.
 */
export const buildUiFlowKeyIndex = (pairs: UiFlowDraftPair[]): UiFlowKeyIndex => {
  const index: UiFlowKeyIndex = { byKey: {}, formUuidByFieldKey: {}, modalKeys: new Set() }

  const walk = (
    draftNode: TemplateConfigInterface,
    persistedNode: TemplateConfigInterface,
    formUuid: string | null,
    inModal: boolean
  ): void => {
    if (!draftNode || !persistedNode) {
      return
    }

    const aiKey = templateConfigAiIdentity(draftNode)
    if (aiKey) {
      index.byKey[aiKey] = { id: persistedNode.id, flowxUuid: persistedNode.flowxUuid }
      if (inModal) {
        index.modalKeys.add(aiKey)
      }
      if (formUuid && FORM_FIELD_IDENTIFIERS.has(draftNode.componentIdentifier)) {
        index.formUuidByFieldKey[aiKey] = formUuid
      }
    }

    const nextFormUuid =
      draftNode.componentIdentifier === ComponentIdentifierEnum.FORM
        ? persistedNode.flowxUuid
        : formUuid
    const nextInModal = inModal || draftNode.componentIdentifier === ComponentIdentifierEnum.MODAL

    const draftChildren = draftNode.templateConfig ?? []
    const persistedChildren = persistedNode.templateConfig ?? []
    for (let i = 0; i < draftChildren.length && i < persistedChildren.length; i++) {
      walk(draftChildren[i], persistedChildren[i], nextFormUuid, nextInModal)
    }
  }

  pairs.forEach(({ draft, persisted, startInModal }) =>
    walk(draft, persisted, null, !!startInModal)
  )

  return index
}

// --- Click-handler synthesis ---

export type UiFlowSynthesizedHandler = {
  templateConfigId: number
  handler: EventHandler
}

/**
 * Synthesizes UI Flow click handlers from the agent's `navigation` + `link_buttons` output:
 * navigation entry → NAVIGATE_TO on the source button (submit buttons carry `formsToSubmit`
 * resolved from their `form_controls`), `cancel` link buttons inside a MODAL → DISMISS.
 * One handler per button; NAVIGATE_TO wins over DISMISS.
 */
export const synthesizeUiFlowEventHandlers = (
  navigation: UiFlowNavigationEntry[] | undefined,
  linkButtons: UiFlowLinkButton[] | undefined,
  index: UiFlowKeyIndex
): UiFlowSynthesizedHandler[] => {
  const handlers: UiFlowSynthesizedHandler[] = []
  const handledButtonKeys = new Set<string>()

  ;(navigation ?? []).forEach((entry) => {
    const button = index.byKey[entry.source_button_key]
    const target = index.byKey[entry.target_page_key]
    if (!button || !target || handledButtonKeys.has(entry.source_button_key)) {
      return
    }

    const linkButton = (linkButtons ?? []).find(
      (candidate) => candidate.button_key === entry.source_button_key
    )
    const formsToSubmit =
      linkButton?.button_type === UiFlowLinkButtonType.Submit
        ? _uniq(
            (linkButton.form_controls ?? [])
              .map((fieldKey) => index.formUuidByFieldKey[fieldKey])
              .filter(Boolean)
          )
        : []

    handledButtonKeys.add(entry.source_button_key)
    handlers.push({
      templateConfigId: button.id,
      handler: {
        type: EventHandlerActionType.NAVIGATE_TO,
        name: '',
        triggerEvent: EventHandlerTriggerEvent.CLICK,
        templateConfigId: button.id,
        params: {
          destination: { uiTemplateFlowxUuid: target.flowxUuid, queryParams: '' },
          formsToSubmit,
          triggerCondition: '',
          analytics: '',
          analyticsCustomPayload: '',
        },
      },
    })
  })
  ;(linkButtons ?? [])
    .filter((linkButton) => linkButton.button_type === UiFlowLinkButtonType.Cancel)
    .filter((linkButton) => !handledButtonKeys.has(linkButton.button_key))
    .filter((linkButton) => index.modalKeys.has(linkButton.button_key))
    .forEach((linkButton) => {
      const button = index.byKey[linkButton.button_key]
      if (!button) {
        return
      }

      handledButtonKeys.add(linkButton.button_key)
      handlers.push({
        templateConfigId: button.id,
        handler: {
          type: EventHandlerActionType.DISMISS,
          name: '',
          triggerEvent: EventHandlerTriggerEvent.CLICK,
          templateConfigId: button.id,
          params: { analytics: '' },
        },
      })
    })

  return handlers
}
